import { Box, Button, Alert, Paper } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { practiceUsersApi } from "apis";
import DashboardLayout from "components/layouts/DashboardLayout";
import { EPath } from "constants/path";
import type {
  ListPracticeUsersRequest,
  ListPracticeUsersRoleEnum,
  ListPracticeUsersStatusEnum,
  ListPracticeUsersSortByEnum,
  ListPracticeUsersSortOrderEnum,
  PracticeUserDto,
} from "libs/cellera-sdk";
import { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { useCelleraTheme } from "../../../theme/useCelleraTheme";
import DeletePracticeUserModal from "./DeletePracticeUserModal";
import LoadingSkeleton from "./LoadingSkeleton";
import PracticeUserFilters from "./PracticeUserFilters";
import PracticeUserSearch from "./PracticeUserSearch";
import PracticeUserTable, { PracticeUser } from "./PracticeUserTable";

interface PracticeUserFilters {
  search: string;
  role: string;
  status: string;
  sortBy: string;
  sortOrder: "asc" | "desc";
  page: number;
  pageSize: number;
}

const PracticeManagement: React.FC = () => {
  const navigate = useNavigate();
  const { colors } = useCelleraTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize filters from URL params or defaults
  const [filters, setFilters] = useState<PracticeUserFilters>(() => ({
    search: searchParams.get("search") || "",
    role: searchParams.get("role") || "",
    status: searchParams.get("status") || "",
    sortBy: searchParams.get("sortBy") || "addedDate",
    sortOrder: (searchParams.get("sortOrder") as "asc" | "desc") || "desc",
    page: parseInt(searchParams.get("page") || "1", 10),
    pageSize: parseInt(searchParams.get("pageSize") || "10", 10),
  }));

  // Debounced search state
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);

  // Delete modal state
  const [deleteModal, setDeleteModal] = useState<{
    open: boolean;
    userId: string;
    userName: string;
  }>({
    open: false,
    userId: "",
    userName: "",
  });

  // Debounce search input with 300ms delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 300);

    return () => clearTimeout(timer);
  }, [filters.search]);

  // Update URL when filters change
  useEffect(() => {
    const newSearchParams = new URLSearchParams();

    if (filters.search) newSearchParams.set("search", filters.search);
    if (filters.role) newSearchParams.set("role", filters.role);
    if (filters.status) newSearchParams.set("status", filters.status);
    if (filters.sortBy !== "addedDate")
      newSearchParams.set("sortBy", filters.sortBy);
    if (filters.sortOrder !== "desc")
      newSearchParams.set("sortOrder", filters.sortOrder);
    if (filters.page !== 1)
      newSearchParams.set("page", filters.page.toString());
    if (filters.pageSize !== 10)
      newSearchParams.set("pageSize", filters.pageSize.toString());

    setSearchParams(newSearchParams, { replace: true });
  }, [filters, setSearchParams]);

  // Memoize query parameters to prevent unnecessary refetches
  const queryParams = useMemo(
    (): ListPracticeUsersRequest => ({
      search: debouncedSearch || undefined,
      role: (filters.role as ListPracticeUsersRoleEnum) || undefined,
      status: (filters.status as ListPracticeUsersStatusEnum) || undefined,
      sortBy: (filters.sortBy as ListPracticeUsersSortByEnum) || undefined,
      sortOrder:
        (filters.sortOrder as ListPracticeUsersSortOrderEnum) || undefined,
      page: filters.page,
      pageSize: filters.pageSize,
    }),
    [
      debouncedSearch,
      filters.role,
      filters.status,
      filters.sortBy,
      filters.sortOrder,
      filters.page,
      filters.pageSize,
    ]
  );

  const {
    data: response,
    isLoading: loading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["practiceUsers", queryParams],
    queryFn: () => practiceUsersApi.listPracticeUsers(queryParams),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Memoize mapped users to prevent unnecessary re-renders
  const practiceUsers = useMemo((): PracticeUser[] => {
    if (!response?.practiceUsers) return [];

    return response.practiceUsers.map((user: PracticeUserDto) => ({
      id: user.id,
      accountId: user.accountId,
      email: user.email,
      name: user.name,
      phoneNumber: user.phoneNumber,
      role: user.role,
      npi: user.npi,
      branches: user.branches,
      specialties: user.specialties,
      status: user.status,
      addedDate: user.addedDate,
    }));
  }, [response?.practiceUsers]);

  // Memoize total count
  const totalCount = useMemo(
    () => response?.pagination?.total || 0,
    [response?.pagination?.total]
  );

  const handleSearchChange = useCallback((search: string) => {
    setFilters((prev) => ({ ...prev, search, page: 1 }));
  }, []);

  const handleFilterChange = useCallback(
    (key: keyof PracticeUserFilters, value: string) => {
      setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
    },
    []
  );

  const handleSortChange = useCallback(
    (sortBy: string, sortOrder: "asc" | "desc") => {
      setFilters((prev) => ({ ...prev, sortBy, sortOrder }));
    },
    []
  );

  const handlePageChange = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setFilters((prev) => ({ ...prev, pageSize, page: 1 }));
  }, []);

  const handleInviteUser = useCallback(() => {
    navigate(EPath.INVITE_PRACTICE);
  }, [navigate]);

  const handleDeleteUser = useCallback((userId: string, userName: string) => {
    setDeleteModal({
      open: true,
      userId,
      userName,
    });
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setDeleteModal({
      open: false,
      userId: "",
      userName: "",
    });
  }, []);

  const handleDeleteSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          {error instanceof Error ? error.message : "Unknown error"}
        </Alert>
      </Box>
    );
  }

  // Show loading skeleton
  if (loading) {
    return (
      <DashboardLayout
        title="Staff management"
        headerActions={
          <Button
            variant="contained"
            className="rounded-full px-6 py-2 font-inter text-white transition-all duration-200 hover:shadow-lg"
            onClick={handleInviteUser}
            sx={{
              backgroundColor: colors.primary.main,
              "&:hover": {
                backgroundColor: colors.primary.dark,
              },
            }}
          >
            Invite
          </Button>
        }
      >
        <Box className="flex flex-col h-full">
          <Paper
            elevation={0}
            sx={{ border: "1px solid #E8E8E9", borderRadius: 2 }}
          >
            <LoadingSkeleton />
          </Paper>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Staff Management"
      headerActions={
        <Button
          variant="contained"
          className="rounded-full px-6 py-2 font-inter text-white transition-all duration-200 hover:shadow-lg"
          onClick={handleInviteUser}
          sx={{
            backgroundColor: colors.primary.main,
            "&:hover": {
              backgroundColor: colors.primary.dark,
            },
          }}
        >
          Invite
        </Button>
      }
    >
      <Box className="flex flex-col h-full">
        <Paper
          elevation={0}
          sx={{ border: "1px solid #E8E8E9", borderRadius: 2, p: 2 }}
        >
          {/* top spacer inside card */}
          <Box className="mb-2" />
          {/* Search and Filters */}
          <Box className="mb-4">
            <Box className="grid grid-cols-2 gap-4 items-stretch">
              <PracticeUserSearch
                value={filters.search}
                onChange={handleSearchChange}
              />
              <Box className="w-full flex justify-end">
                <PracticeUserFilters
                  role={filters.role}
                  status={filters.status}
                  onRoleChange={(role) => handleFilterChange("role", role)}
                  onStatusChange={(status) =>
                    handleFilterChange("status", status)
                  }
                />
              </Box>
            </Box>
          </Box>

          {/* Table */}
          <Box className="flex-1">
            <PracticeUserTable
              practiceUsers={practiceUsers}
              loading={loading}
              sortBy={filters.sortBy}
              sortOrder={filters.sortOrder}
              onSortChange={handleSortChange}
              onUserClick={(userId) => {
                navigate(EPath.PRACTICE_DETAIL.replace(":practiceId", userId));
              }}
              onDeleteUser={handleDeleteUser}
              pagination={{
                page: filters.page,
                pageSize: filters.pageSize,
                total: totalCount,
                onPageChange: handlePageChange,
                onPageSizeChange: handlePageSizeChange,
              }}
            />
          </Box>
        </Paper>
      </Box>

      <DeletePracticeUserModal
        open={deleteModal.open}
        onClose={handleCloseDeleteModal}
        userId={deleteModal.userId}
        userName={deleteModal.userName}
        onDeleteSuccess={handleDeleteSuccess}
      />
    </DashboardLayout>
  );
};

export default PracticeManagement;
